import type { LogoVariante } from "@kundeninfo/api-client";

/**
 * Woher ein Unternehmenslogo kommt — „hochgeladen vor Datei" (Spec Unternehmens-
 * Verwaltung), je Variante EINZELN entschieden.
 *
 * Die Auflösung wohnt im Web und nicht in der API: Die statischen Dateien liegen im
 * Auslieferungspaket der Oberfläche und werden vom Webserver ausgeliefert, nicht aus
 * `files.root`. Die API kennt deshalb nur die hochgeladene Datei
 * (`GET /api/companies/:id/logo/:variante`, 404 ohne sie) und sagt an jeder
 * Unternehmens-Antwort über `hochgeladeneLogos`, ob es dort etwas zu holen gibt — damit
 * die Oberfläche nicht ins Leere greifen und einen Fehlversuch abwarten muss.
 *
 * Je Variante einzeln, weil die Spec einen SCHRITTWEISEN Ersatz beschreibt: Ein
 * Unternehmen darf seine helle Wortmarke hochgeladen haben und für die dunkle noch beim
 * Bestand sein. Eine Entscheidung „alles oder nichts" zwänge dazu, alle vier auf einmal
 * zu tauschen.
 */

/**
 * Ein Unternehmen, so weit es für die Logo-Auflösung zählt. Absichtlich strukturell und
 * mit weglassbaren Dateinamen: Es passt sowohl die öffentliche Gestalt `Unternehmen` als
 * auch die Verwaltungsgestalt `UnternehmenStammdaten` hinein. BEIDE führen die vier
 * statischen Namen seit dem 11.09.2026 — bis dahin schnitt der Serialisierer der
 * Verwaltungsantwort sie weg, und die Verwaltungsmaske schrieb deshalb „kein Logo" an
 * eine Variante, für die die Kopfzeile derselben Anwendung ein Bild zeigte. Weglassbar
 * bleiben sie trotzdem, weil ein Aufrufer mit einem schmaleren Objekt (ein Test, eine
 * künftige Magergestalt) hier nicht scheitern soll: Fehlt der Name, gibt es eben keine
 * Standarddatei.
 */
export type LogoTraeger = {
  id: string;
  hochgeladeneLogos: readonly LogoVariante[];
  logoWordmark?: string | null;
  logoWordmarkDark?: string | null;
  logoMark?: string | null;
  logoMarkDark?: string | null;
};

/**
 * Der Auslieferungspfad einer HOCHGELADENEN Variante. Er wird direkt als `<img src>`
 * gesetzt und läuft bewusst nicht über den Client: Bilder holt der Browser selbst, ein
 * Blob-Umweg brächte nur eine zweite Zwischenablage (siehe `uploadCompanyLogo`).
 */
export function hochgeladenesLogoUrl(id: string, variante: LogoVariante): string {
  return `/api/companies/${encodeURIComponent(id)}/logo/${variante}`;
}

/** Welcher statische Dateiname zu welcher Variante gehört — eine Stelle, nicht vier
 *  verstreute Ternäroperatoren. */
const STATISCHES_FELD: Record<LogoVariante, keyof LogoTraeger> = {
  "wortmarke": "logoWordmark",
  "wortmarke-dunkel": "logoWordmarkDark",
  "bildmarke": "logoMark",
  "bildmarke-dunkel": "logoMarkDark",
};

/**
 * Das WIRKSAME Logo einer Variante — mitsamt seiner Herkunft.
 *
 * Warum die Herkunft mitreist und nicht nur die Adresse: Die Verwaltungsmaske muss zwei
 * Dinge auseinanderhalten, die dasselbe Bild ergeben. „Hochgeladen" lässt sich ENTFERNEN
 * (danach fällt die Zeile auf die Standarddatei zurück), eine Standarddatei nicht — sie
 * liegt im Auslieferungspaket der Oberfläche und nicht in der Ablage. Ein blosser Pfad
 * zwänge die Maske, aus `/api/…` gegen `/logos/…` zurückzuraten; das ist genau die Sorte
 * abgeleitetes Wissen, die beim nächsten Pfadwechsel still falsch wird.
 *
 * `null` heisst: für diese Variante gibt es nichts — weder hochgeladen noch als Datei.
 */
export type LogoWirksam =
  | { url: string; herkunft: "hochgeladen" }
  | { url: string; herkunft: "datei"; datei: string };

export function logoWirksam(
  firma: LogoTraeger, variante: LogoVariante,
): LogoWirksam | null {
  if (firma.hochgeladeneLogos.includes(variante)) {
    return { url: hochgeladenesLogoUrl(firma.id, variante), herkunft: "hochgeladen" };
  }
  const datei = firma[STATISCHES_FELD[variante]];
  if (typeof datei !== "string" || datei === "") return null;
  return { url: `/logos/${datei}`, herkunft: "datei", datei };
}

/**
 * Nur die Bildquelle einer Variante — für alle, die die Herkunft nicht brauchen (die
 * Kopfzeile zeigt schlicht das Bild und sonst das Kürzel auf der Markenfarbe). Eine
 * zweite Auflösung wäre eine zweite Wahrheit: Sie steht bewusst auf `logoWirksam`.
 */
export function logoQuelle(firma: LogoTraeger, variante: LogoVariante): string | null {
  return logoWirksam(firma, variante)?.url ?? null;
}
